/**
 * Tarjetas de repaso del curso.
 *
 * Sustituye a useFlashcards, que leia las preguntas del banco en
 * ficheros y guardaba el avance en localStorage con una clave
 * heredada de PV-Learn. Eso hacia que el repaso se perdiera al
 * cambiar de navegador.
 *
 * Ahora las preguntas salen de la base de datos, igual que el
 * ejercicio de cada leccion, y el estado vive en la cuenta.
 */

import { useMemo } from 'react'
import { useNow } from '@/hooks/useNow'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { getSupabase, isSupabaseEnabled } from '@/lib/supabase'
import { fetchCourseQuestions, type CourseQuestion } from '@/lib/blockDb'

export interface ReviewCard {
  question: CourseQuestion
  dueAt: Date
  repetitions: number
  /** Si nunca se ha repasado, entra directa a la cola. */
  isNew: boolean
}

interface CardRow {
  question_id: string
  repetitions: number
  due_at: string
}

async function fetchCards(): Promise<Map<string, CardRow>> {
  const out = new Map<string, CardRow>()
  if (!isSupabaseEnabled()) return out
  const supabase = getSupabase()
  if (!supabase) return out

  const { data, error } = await supabase
    .from('review_card')
    .select('question_id, repetitions, due_at')

  if (error || !data) return out
  for (const r of data) out.set(r.question_id as string, r as unknown as CardRow)
  return out
}

/** Registra una revision. quality: 0 fallo, 3 dificil, 5 facil. */
async function review(questionId: string, quality: number): Promise<void> {
  if (!isSupabaseEnabled()) return
  const supabase = getSupabase()
  if (!supabase) return

  const { error } = await supabase.rpc('review_question', {
    p_question_id: questionId,
    p_quality: quality,
  })
  if (error) console.info('[repaso] review:', error.message)
}

export function useReviewDeck(courseSlug: string) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const now = useNow(60_000)

  const { data: questions = [], isLoading: loadingQuestions } = useQuery({
    queryKey: ['course-questions', courseSlug],
    queryFn: () => fetchCourseQuestions(courseSlug),
    enabled: Boolean(courseSlug),
    staleTime: 5 * 60 * 1000,
  })

  const { data: cards = new Map<string, CardRow>(), isLoading: loadingCards } = useQuery({
    queryKey: ['review-cards', user?.id],
    queryFn: fetchCards,
    enabled: Boolean(user?.id),
    staleTime: 30_000,
  })

  const deck = useMemo<ReviewCard[]>(() => {
    return questions
      .map(q => {
        const card = cards.get(q.id)
        return {
          question: q,
          dueAt: card ? new Date(card.due_at) : new Date(0),
          repetitions: card?.repetitions ?? 0,
          isNew: !card,
        }
      })
      .sort((a, b) => a.dueAt.getTime() - b.dueAt.getTime())
  }, [questions, cards])

  /** Las que tocan hoy: nuevas, o con la fecha ya pasada. */
  const due = useMemo(
    () => deck.filter(c => c.isNew || c.dueAt.getTime() <= now),
    [deck, now],
  )

  const reviewMut = useMutation({
    mutationFn: (v: { questionId: string; quality: number }) =>
      review(v.questionId, v.quality),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['review-cards', user?.id] }),
  })

  return {
    all: deck,
    due,
    dueCount: due.length,
    isLoading: loadingQuestions || loadingCards,
    reviewCard: (questionId: string, quality: number) =>
      reviewMut.mutate({ questionId, quality }),
  }
}
